/**
 * CopilotReportWindow - Copilot 报告独立窗口组件
 * 使用 DragResizeBox 实现可拖拽和可调整大小的报告窗口
 */
import React, { useCallback, useMemo, useRef } from 'react';
import DragResizeBox, { DragDirection } from '../../../components/DragResizeBox/DragResizeBox';
import { ReportPanel } from '../../../components/ReportViewer';
import { IReportData, IReportUpdateParams } from '../../../components/ReportViewer/types';
import { ChatSmartTable } from '../../../ChatSmartTable';
import { MagicPosterPanel } from '../../../ChatDSTemplate/components/ChatMagicPoster';
import { AiGalleryPanel } from '../../../ChatDSTemplate/components/AiGallery/AiGalleryPanel';
import { ChatContentType } from '../../../ChatDSTemplate/type';
import { getCosReportPanelKey } from '../../../ChatDSTemplate/utils';
import './CopilotReportWindow.scss';

/** 小窗口内容面板组件映射（参照 ChatDSTemplate 的 CHAT_CONTENT_PANEL_MAP） */
const COPILOT_CONTENT_PANEL_MAP = {
  [ChatContentType.Document]: ReportPanel,
  [ChatContentType.SmartTable]: ChatSmartTable,
  [ChatContentType.MagicPoster]: MagicPosterPanel,
  [ChatContentType.AiGallery]: AiGalleryPanel,
};

// 尺寸限制常量
const SCREEN_MARGIN = 15;
const MIN_WIDTH = 600;
const INITIAL_WIDTH = 800;
const NAV_BAR_HEIGHT = 48;
/** 报告窗口相对于屏幕右侧的额外偏移，为 Copilot 会话窗口留出空间（520(Copilot宽度) + 15(边距) + 20(间距) - 15(右边距) = 540） */
const COPILOT_WINDOW_OFFSET = 540;

/** 动态计算尺寸限制，窗口大小变化时可获取最新值 */
const getWindowSizeConstraints = () => ({
  maxWidth: window.innerWidth * 0.85,
  minHeight: window.innerHeight * 0.5,
  maxHeight: window.innerHeight - NAV_BAR_HEIGHT,
  initialHeight: window.innerHeight - NAV_BAR_HEIGHT,
});

type DragResizeBoxRefType = {
  dropAreaRef: React.MutableRefObject<HTMLDivElement | null>;
  handleMouseDown: (direction: DragDirection, e: React.MouseEvent) => void;
};

export interface ICopilotReportWindowProps {
  /** 是否可见 */
  visible: boolean;
  /** 会话 ID */
  sessionId: string;
  /** 会话列表 */
  conversations: unknown[];
  /** 报告数据 */
  dsReport: IReportData;
  /** 流式状态 */
  streamingState: string;
  /** 报告更新回调 */
  handleUpdateDsReport: (update: IReportUpdateParams) => void;
  /** 开始新对话回调 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  startNewConversation: (...args: any[]) => void;
  /** 窗口聚焦回调 */
  onFocus?: () => void;
  /** z-index 值 */
  zIndex?: number;
  /** 关闭回调 */
  onClose?: () => void;
}

export const CopilotReportWindow: React.FC<ICopilotReportWindowProps> = ({
  visible,
  sessionId,
  conversations,
  dsReport,
  streamingState,
  handleUpdateDsReport,
  startNewConversation,
  onFocus,
  zIndex = 1990,
  onClose,
}) => {
  const dragResizeBoxRef = useRef<DragResizeBoxRefType>(null);

  // 处理头部拖拽
  const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    // 检查是否点击了可交互元素，如果是则不触发拖拽
    const target = e.target as HTMLElement;
    const isInteractiveElement = 
      target.closest('button') ||
      target.closest('[role="button"]') ||
      target.closest('.tea-dropdown') ||
      target.closest('.tea-tabs__tabitem') ||
      target.closest('.dba-report-step-tabs__close') ||
      target.closest('.dba-report-toolbar') ||
      target.closest('.dba-report-file-selector');
    
    if (!isInteractiveElement) {
      // 触发 DragResizeBox 的拖拽
      dragResizeBoxRef.current?.handleMouseDown('move', e);
    }
  }, []);

  // 处理报告更新，支持关闭
  const handleReportUpdate = useCallback((update: IReportUpdateParams) => {
    handleUpdateDsReport(update);
    if (update.show === false) {
      onClose?.();
    }
  }, [handleUpdateDsReport, onClose]);

  // 当前面板组件类型（参照 ChatDSTemplate 的做法）
  const contentType = useMemo(() => {
    return ((dsReport as any)?.componentType || ChatContentType.Document) as ChatContentType;
  }, [dsReport]);

  // 根据 type 获取对应的面板组件，默认为 Document 类型
  const PanelComponent = useMemo(() => {
    return COPILOT_CONTENT_PANEL_MAP[contentType] || COPILOT_CONTENT_PANEL_MAP[ChatContentType.Document];
  }, [contentType]);

  // 智能表格在 Copilot 浮动窗口中为只读预览，与 ChatPage 分栏（template=splitView -> readonly）语义一致；
  // Copilot 页 URL 无 template 参数，故直接按组件类型判断，其他面板保持原行为
  const isSmartTablePanel = contentType === ChatContentType.SmartTable;

  // 面板组件的公共 props（各面板组件所需的核心 props 保持一致）
  const panelProps = useMemo(() => ({
    sessionId,
    conversations,
    dsReport,
    streamingState,
    handleUpdateDsReport: handleReportUpdate,
    startNewConversation,
    readonly: isSmartTablePanel,
    showCloseButton: true,
    onHeaderMouseDown: handleHeaderMouseDown,
    onFocus,
  }), [contentType, sessionId, conversations, dsReport, streamingState, handleReportUpdate, startNewConversation, handleHeaderMouseDown, onFocus]);

  if (!visible) {
    return null;
  }

  const { maxWidth, minHeight, maxHeight, initialHeight } = getWindowSizeConstraints();

  return (
    <DragResizeBox
      defaultWidth={INITIAL_WIDTH}
      defaultHeight={initialHeight}
      defaultX={window.innerWidth - INITIAL_WIDTH - SCREEN_MARGIN - COPILOT_WINDOW_OFFSET}
      defaultY={SCREEN_MARGIN + 60}
      minWidth={MIN_WIDTH}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      ref={dragResizeBoxRef}
      className="dba-copilot-report-window"
      safeArea={{
        top: 65,
        right: SCREEN_MARGIN,
        bottom: SCREEN_MARGIN,
        left: SCREEN_MARGIN,
      }}
      // 动态层级
      style={{ zIndex }}
      // 拉伸开始时提升层级
      onResizeStart={onFocus}
    >
      <div className="dba-copilot-report-window__content">
        <div className="dba-copilot-report-window__panel">
          <PanelComponent key={getCosReportPanelKey(dsReport)} {...panelProps} />
        </div>
      </div>
    </DragResizeBox>
  );
};

export default CopilotReportWindow;
